import { useId, useState, type ComponentProps, type ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * Bloque del sistema visual: toda cifra y toda tabla viven dentro de uno. `tone="light"` lo pone
 * en blanco con texto negro.
 *
 * **Ya no es «uno por pantalla».** Lo era, para que destacara; el owner lo cambió el 26-sep-2026
 * mirando la ficha de una cotización: sobre el panel oscuro las cifras se le veían «todo negro,
 * los textos solapados y una barra rara». La regla nueva es al revés — **el dato vive sobre
 * blanco**, y el fondo oscuro es el marco, no el soporte.
 *
 * Con ella vienen otras tres, y valen para todo lo que se construya: cada bloque lleva un
 * elemento gráfico que ayude a leerlo (diagrama, esquema o icono), el contraste es alto —una
 * barra fina y tenue no se ve, y lo que no se ve no informa— y las tablas van cerradas con sus
 * bordes. El criterio: **que se entienda de un vistazo, sin leer.**
 */
export function Panel({ className, tone, ...props }: ComponentProps<"section"> & { tone?: "light" }) {
  return <section className={cn("panel p-5 sm:p-7", tone === "light" && "panel-light", className)} {...props} />;
}

/**
 * El encabezado de un panel: su título, y lo que haga falta para entenderlo.
 *
 * **`hint` y `ayuda` no son lo mismo, y confundirlos es la regla 1 de las reglas de pantalla.**
 *
 * `hint` es un dato: una frase corta que dice *qué se está mirando* y que hace falta cada vez que se
 * mira («Los pedidos de esta semana, primero lo que más corre»). `ayuda` es una **explicación**: dice
 * *cómo funciona* lo que hay debajo, se lee una vez en la vida y luego se queda ahí ocupando pantalla
 * para siempre, y quien ya lo sabe tiene que saltársela cada vez que abre la pantalla.
 *
 * Por eso `ayuda` vive detrás de un «?» y `hint` no. Si dudas de cuál de los dos es lo que estás
 * escribiendo: si la frase empieza por «Una fila por…», «Aquí se…» o «Sirve para…», es `ayuda`.
 */
export function PanelHeader({
  title,
  hint,
  ayuda,
  action,
}: {
  title: string;
  hint?: string;
  /** Cómo funciona lo de abajo. Va detrás de un «?»: se lee una vez y luego sobra. */
  ayuda?: string;
  action?: ReactNode;
}) {
  const [abierta, setAbierta] = useState(false);
  const id = useId();
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-medium tracking-tight text-ink">{title}</h2>
          {ayuda ? (
            <button
              type="button"
              onClick={() => setAbierta((v) => !v)}
              aria-expanded={abierta}
              aria-controls={id}
              /* El nombre dice de qué va a hablar: «?» a secas no se puede buscar ni con un lector de
                 pantalla ni con el teclado, y en una pantalla con cuatro paneles hay cuatro «?». */
              aria-label={`Cómo funciona: ${title}`}
              className="relative size-5 shrink-0 rounded-full border border-line max-sm:after:absolute max-sm:after:-inset-3 text-xs leading-none text-ink-2 hover:border-ink-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              ?
            </button>
          ) : null}
        </div>
        {hint ? <p className="mt-1 max-w-3xl text-sm text-ink-2">{hint}</p> : null}
        {ayuda && abierta ? (
          <p id={id} className="mt-1 max-w-3xl text-sm text-ink-2">
            {ayuda}
          </p>
        ) : null}
      </div>
      {action}
    </header>
  );
}
